// Where the cover artwork actually goes on the vinyl wrap.
//
// SINGLE SOURCE OF TRUTH: derived from the admin PrintSettings via
// computeTemplateLayout — the same calculation the printed guide lines and the
// fiche de montage use, so artwork and guides can never drift apart again.
//
// Before this existed, generateCoverPdf placed artwork with A4_W (210mm) and
// A3PLUS_H (297mm) while the real board is 200 × 303mm. The guides were right
// and the picture was wrong: 10mm of photo hung past the fore-edge, the back
// panel sat 10mm off, and a bare strip was left at head and tail. See
// coverPanelGeometry.test.ts for the exact numbers.
//
// Do NOT reintroduce A4_W / A3PLUS_H here. A4_H (297mm) is the INTERIOR page
// height and must stay untouched — the two specs are 6mm apart and swapping
// them is precisely the bug this module exists to prevent.
import { computeTemplateLayout, type PrintSettings } from './assemblyTemplate';

/** mm → PDF points. Matches MM_TO_PT in pdfGenerator.ts / MM in assemblyTemplate.ts. */
export const MM_TO_PT_ = 2.8346;

// ── THE IMAGE FRAME (Yahya's spec) ───────────────────────────────────────────
// A cover image the customer adds — front or back — is placed in a 200 × 300mm
// frame plus bleed, i.e. 206 × 306mm of artwork.
//
// This is DELIBERATELY decoupled from the board (200 × 303mm) and from the
// vinyl wrap (482 × 359mm), both of which are physical and UNCHANGED. The frame
// is simply where the picture sits; it is centred on the board and its bleed
// runs past every board edge, so nothing bare can trim in:
//   height 306 vs board 303 → 1.5mm spare at head and tail
//   width  206 vs board 200 → 3mm spare at each side
export const COVER_IMAGE_FRAME_W_MM = 200;
export const COVER_IMAGE_FRAME_H_MM = 300;

/** Bleed past every frame edge (mm), so the wrap can be folded and trimmed.
 *  The original code used `BLEED_PT = 6` — six POINTS (~2.1mm) that read like
 *  millimetres — and applied it top/bottom only. Millimetres, all four sides. */
export const COVER_BLEED_MM = 3;

export interface CoverPanelGeometry {
  /** Artwork panel size (pt) = the physical board. */
  panelW: number;
  panelH: number;
  /** Left edge of the back / front artwork panels (pt). */
  backX: number;
  frontX: number;
  /** Printed spine column: board + both hinge gaps (pt). */
  spineX: number;
  spineW: number;
  /** Bottom of both panels (pt) — boards are centred on the sheet height. */
  panelY: number;
  /** Bleed in points, applied outward on all four edges. */
  bleed: number;
  /** Panel width / height — the ratio the editor preview must use so that
   *  object-cover crops on screen exactly as cover-fit crops in the PDF. */
  aspect: number;
}

export function coverPanelGeometry(s: PrintSettings): CoverPanelGeometry {
  const L = computeTemplateLayout(s);
  const panelW = COVER_IMAGE_FRAME_W_MM * MM_TO_PT_;
  const panelH = COVER_IMAGE_FRAME_H_MM * MM_TO_PT_;
  // Centre the frame on each board. The boards keep their real size and place
  // on the wrap; only the picture is framed. With a 200 × 300 frame on a
  // 200 × 303 board this nudges the artwork 1.5mm up from the board's bottom.
  const insetX = (s.boardWmm * MM_TO_PT_ - panelW) / 2;
  const insetY = (s.boardHmm * MM_TO_PT_ - panelH) / 2;
  return {
    panelW,
    panelH,
    backX:  L.backBoardL + insetX,
    frontX: L.frontBoardL + insetX,
    spineX: L.spineColL,
    spineW: L.spineColR - L.spineColL,
    panelY: L.boardBottom + insetY,
    bleed:  COVER_BLEED_MM * MM_TO_PT_,
    aspect: COVER_IMAGE_FRAME_W_MM / COVER_IMAGE_FRAME_H_MM,
  };
}
